import { getPostAliasKey } from '../../utils/laymenAlias';
import { extractPlainText } from '../../utils/richText';

export const GRADE_LABELS = Object.freeze({
    1: 'Speculative',
    2: 'Emerging',
    3: 'Sourced',
    4: 'Reframed',
    5: 'Established',
});

export const clampGrade = (value) => {
    const num = Math.round(Number(value));
    if (!Number.isFinite(num)) {
        return 1;
    }
    return Math.min(5, Math.max(1, num));
};

// Parse the post's display date ("YYYY.MM.DD") with createdAt as fallback.
export const getPostDate = (post) => {
    if (post && typeof post.date === 'string') {
        const match = post.date.trim().match(/^(\d{4})\.(\d{2})\.(\d{2})$/);
        if (match) {
            const parsed = new Date(Date.UTC(
                Number(match[1]),
                Number(match[2]) - 1,
                Number(match[3])
            ));
            if (!Number.isNaN(parsed.getTime())) {
                return parsed;
            }
        }
    }
    if (post?.createdAt) {
        const created = new Date(post.createdAt);
        if (!Number.isNaN(created.getTime())) {
            return created;
        }
    }
    return null;
};

const CARD_DATE_OPTIONS = { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' };
const FULL_DATE_OPTIONS = { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' };

// "Jul 13, 2026" — card format on the live site.
export const formatCardDate = (post) => {
    const date = getPostDate(post);
    return date ? date.toLocaleDateString('en-US', CARD_DATE_OPTIONS) : '';
};

// "June 30, 2026" — single-article format on the live site.
export const formatFullDate = (post) => {
    const date = getPostDate(post);
    return date ? date.toLocaleDateString('en-US', FULL_DATE_OPTIONS) : '';
};

export const getPostDateTimeAttr = (post) => {
    const date = getPostDate(post);
    return date ? date.toISOString() : undefined;
};

export const countWords = (html) => {
    const plain = extractPlainText(html);
    if (!plain) {
        return 0;
    }
    return plain.split(/\s+/).filter(Boolean).length;
};

// Live site formula: floor(words / 200), minimum 1 — e.g. 6934 words → "34 min read".
export const readMinutesFromWords = (wordCount) => {
    const words = Number(wordCount) || 0;
    return Math.max(1, Math.floor(words / 200));
};

export const getPostWordCount = (post) => {
    if (Number.isFinite(post?.wordCount)) {
        return post.wordCount;
    }
    return countWords(post?.content || '');
};

export const formatReadTime = (post) => `${readMinutesFromWords(getPostWordCount(post))} min read`;

// Deterministic "Laymen NNN" pseudonym per author key (stable across loads,
// unlike the session-random generator the legacy app uses).
const hashString = (value) => {
    let hash = 0x811c9dc5;
    for (let i = 0; i < value.length; i += 1) {
        hash ^= value.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    return hash >>> 0;
};

export const getLaymenNumber = (post) => {
    const key = getPostAliasKey(post) || 'anonymous';
    return 1 + (hashString(String(key)) % 2100);
};

// Posts store a display alias (e.g. "LAYMEN 1"); use it when present,
// otherwise fall back to a stable pseudonym derived from the author key.
export const formatByline = (post) => {
    const author = typeof post?.author === 'string' ? post.author.trim() : '';
    if (author && author.toUpperCase() !== 'ANONYMOUS') {
        return author;
    }
    return `Laymen ${getLaymenNumber(post)}`;
};

// Series detection from titles like "WORLD WAR LIES — PART 2: ..." or
// "THE ENGINEERED SOUL, PART III" (live titles use both digits and Roman numerals).
const SERIES_PATTERN = /^(.*?)[\s—–\-:,|(]*\bPART\s+(\d+|[IVXLC]+)\b/i;

const ROMAN_VALUES = { i: 1, v: 5, x: 10, l: 50, c: 100 };

const parsePartNumber = (raw) => {
    if (/^\d+$/.test(raw)) {
        return Number(raw);
    }
    let total = 0;
    let prev = 0;
    const chars = raw.toLowerCase().split('').reverse();
    for (const char of chars) {
        const value = ROMAN_VALUES[char];
        if (!value) {
            return NaN;
        }
        if (value < prev) {
            total -= value;
        } else {
            total += value;
            prev = value;
        }
    }
    return total;
};

export const parseSeriesFromTitle = (title) => {
    if (typeof title !== 'string') {
        return null;
    }
    const match = title.match(SERIES_PATTERN);
    if (!match) {
        return null;
    }
    const base = match[1].replace(/[\s—–\-:,|(]+$/, '').trim();
    const part = parsePartNumber(match[2]);
    if (!base || !Number.isFinite(part) || part < 1) {
        return null;
    }
    return {
        name: base,
        key: base.toLowerCase(),
        part,
    };
};

const compareSeriesNames = (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });

export const buildSeriesList = (posts) => {
    const seriesMap = new Map();

    (Array.isArray(posts) ? posts : []).forEach((post) => {
        const parsed = parseSeriesFromTitle(post?.title);
        if (!parsed) {
            return;
        }
        if (!seriesMap.has(parsed.key)) {
            seriesMap.set(parsed.key, { key: parsed.key, name: parsed.name, parts: [] });
        }
        seriesMap.get(parsed.key).parts.push({
            postId: post._id,
            part: parsed.part,
            title: post.title,
        });
    });

    const series = [...seriesMap.values()];
    series.forEach((entry) => {
        entry.parts.sort((a, b) => a.part - b.part || String(a.postId).localeCompare(String(b.postId)));
    });
    series.sort(compareSeriesNames);
    return series;
};

// Writer-created series from /api/series. Parts follow the stored order and
// skip ids missing from the loaded posts (deleted, or not in the index yet);
// a post claimed by an earlier series is skipped too, so every post maps to
// at most one series. Series left without parts are dropped.
export const buildExplicitSeries = (explicit, posts) => {
    const postById = new Map(
        (Array.isArray(posts) ? posts : [])
            .filter((post) => post && post._id)
            .map((post) => [String(post._id), post])
    );
    const claimed = new Set();

    return (Array.isArray(explicit) ? explicit : [])
        .map((entry) => {
            const slug = typeof entry?.slug === 'string' ? entry.slug.trim() : '';
            const name = typeof entry?.name === 'string' ? entry.name.trim() : '';
            if (!slug || !name) {
                return null;
            }
            const parts = [];
            (Array.isArray(entry.postIds) ? entry.postIds : []).forEach((rawId) => {
                const id = String(rawId);
                const post = postById.get(id);
                if (!post || claimed.has(id)) {
                    return;
                }
                claimed.add(id);
                parts.push({ postId: post._id, part: parts.length + 1, title: post.title });
            });
            if (parts.length === 0) {
                return null;
            }
            return {
                key: `s:${slug}`,
                slug,
                name,
                description: typeof entry.description === 'string' ? entry.description.trim() : '',
                explicit: true,
                parts,
            };
        })
        .filter(Boolean);
};

// Writer-created series plus title-detected ones ("… PART 2"). A post a
// writer put in a series is never also title-detected: explicit wins.
export const mergeSeries = (explicit, posts) => {
    const explicitSeries = buildExplicitSeries(explicit, posts);
    const claimedIds = new Set(
        explicitSeries.flatMap((entry) => entry.parts.map((part) => String(part.postId)))
    );
    const detectedSeries = buildSeriesList(
        (Array.isArray(posts) ? posts : []).filter((post) => !claimedIds.has(String(post?._id)))
    ).map((entry) => ({ ...entry, slug: null, description: '', explicit: false }));

    return [...explicitSeries, ...detectedSeries].sort(compareSeriesNames);
};

// Writer series have their own page; detected ones open on their first part.
export const getSeriesPath = (series) => {
    if (series?.slug) {
        return `/series/${encodeURIComponent(series.slug)}`;
    }
    const firstPart = series?.parts?.[0];
    return firstPart ? `/posts/${firstPart.postId}` : '/series';
};

// The parts just before and after `postId` in reading order.
export const getSeriesNeighbors = (series, postId) => {
    const parts = Array.isArray(series?.parts) ? series.parts : [];
    const index = parts.findIndex((part) => String(part.postId) === String(postId));
    if (index < 0) {
        return { index: -1, previous: null, next: null };
    }
    return {
        index,
        previous: parts[index - 1] || null,
        next: parts[index + 1] || null,
    };
};

// KJV verse bar — verses observed on the live site, picked per-route.
const KJV_VERSES = Object.freeze([
    {
        text: '“The heart is deceitful above all things, and desperately wicked: who can know it?”',
        ref: 'Jeremiah 17:9',
    },
    {
        text: '“The simple believeth every word: but the prudent man looketh well to his going.”',
        ref: 'Proverbs 14:15',
    },
    {
        text: '“A wise man will hear, and will increase learning; and a man of understanding shall attain unto wise counsels.”',
        ref: 'Proverbs 1:5',
    },
    {
        text: '“And have no fellowship with the unfruitful works of darkness, but rather reprove them.”',
        ref: 'Ephesians 5:11',
    },
    {
        text: '“And Jesus answered and said unto them, Take heed that no man deceive you.”',
        ref: 'Matthew 24:4',
    },
    {
        text: '“For the eyes of the LORD run to and fro throughout the whole earth.”',
        ref: 'II Chronicles 16:9',
    },
]);

export const getVerseForPath = (pathname) => {
    const key = typeof pathname === 'string' ? pathname : '/';
    return KJV_VERSES[hashString(key) % KJV_VERSES.length];
};
